import { useCallback, useMemo } from 'react';
import type { FormEvent, MutableRefObject } from 'react';

import type { PMSVendorData } from '@/types/pms.types';
import AppfolioConnection from '@/components/integrations/appfolio/appfolio-connection/AppfolioConnection';
import AppFolioOAuthConnection from '@/components/integrations/appfolio/appfolio-oauth-connection/AppFolioOAuthConnection';
import UserAppfolio from '@/components/integrations/appfolio/user-appfolio-view/UserAppfolio';
import { CredentialSummary, credentialRows } from '../../components/CredentialSummary';
import { credentialKindFor, usesOAuthView } from '../credentialKind';
import type { ConnectionPanelProps } from '../types';

/** AppFolio Passport sign-in — where a non-admin enables the integration. */
const APPFOLIO_PASSPORT_URL = 'https://passport.appf.io/sign_in?idp_type=property&vhostless=true';

const AppfolioConnectionPanel = ({ ctx }: ConnectionPanelProps) => {
  const {
    account, vendorData, setVendorData, connectionState, setConnectionState, connected, setConnected,
    submitRef, submitCredentials, redirectToHowToConfigure, formSubmitAttempted,
    setToggleLoader, editedSettings, seedSettings, isInternalAdmin,
    mode, credentialsFormKey, pendingCredentials, setCredentialsDirty,
    saveAndSync, saveWithoutSync, integrationStatus,
  } = ctx;
  const useOAuth = useMemo(() => usesOAuthView(vendorData?.secret), [vendorData]);

  // The three views replace the record via this setter; keep settings seeded from it.
  const setVenderListData = (data: PMSVendorData) => {
    setVendorData(data);
    seedSettings(data);
  };

  // Non-admin connected save (no form): the same direct path the old footer took.
  const saveStored = useCallback(() => {
    // masked means keep, server-side: a masked or blank key/secret is replaced by the stored credential in property-service, matched on entity and vendor (DV-3414)
    const configData: PMSVendorData = { url: '', key: '', secret: '', ...vendorData, ...editedSettings };
    if (integrationStatus.kind === 'disabled') void saveWithoutSync(configData);
    else saveAndSync(configData);
  }, [editedSettings, integrationStatus.kind, saveAndSync, saveWithoutSync, vendorData]);

  const shared = {
    account,
    connectionState, setConnectionState, setToggleLoader,
    redirectToHowToConfigure: (_open?: boolean) => redirectToHowToConfigure(),
    autoSyncAssets: editedSettings.autoSyncAssets,
    watermarkedPhotos: editedSettings.watermarkedPhotos,
    pmsSource: 'appfolio',
    formSubmit: formSubmitAttempted,
    initialVendorData: vendorData,
    setVenderListData,
  };

  // Spec §5.3: read mode shows the masked summary. The views below stay
  // mounted either way — their on-mount credential test must still run — they
  // simply render no form in read mode.
  const summary =
    mode === 'read' && vendorData ? (
      <div className="px-4">
        <CredentialSummary
          vendorName="AppFolio"
          rows={credentialRows(credentialKindFor('appfolio', vendorData), 'AppFolio', vendorData)}
        />
      </div>
    ) : null;

  const form = useOAuth ? (
    <AppFolioOAuthConnection
      {...shared}
      mode={mode}
      formKey={credentialsFormKey}
      pendingCredentials={pendingCredentials}
      onDirtyChange={setCredentialsDirty}
      connected={connected}
      setConnected={setConnected}
      onSubmit={submitCredentials}
      // Documented, banked cast: this view's ref prop carries Formik's
      // `handleSubmit` signature (an optional FormEvent), while the context's
      // `submitRef` is the bar's plain `() => void`. Calling it with no
      // argument — all the bar ever does — is safe.
      configConnectionFormRef={
        submitRef as MutableRefObject<((e?: FormEvent<HTMLFormElement>) => void) | undefined>
      }
    />
  ) : !isInternalAdmin ? (
    <UserAppfolio
      {...shared}
      submitRef={submitRef}
      onSaveStored={saveStored}
      passportUrl={APPFOLIO_PASSPORT_URL}
    />
  ) : (
    <AppfolioConnection
      {...shared}
      mode={mode}
      formKey={credentialsFormKey}
      pendingCredentials={pendingCredentials}
      onDirtyChange={setCredentialsDirty}
      connected
      setConnected={setConnected}
      onSubmit={submitCredentials}
      configConnectionFormRef={submitRef}
    />
  );

  return (
    <>
      {summary}
      {form}
    </>
  );
};

export default AppfolioConnectionPanel;
